import {
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  SimpleGrid,
} from "@chakra-ui/react";
import { Box, Button, Select } from "@chakra-ui/react";
import { AlertsContext } from "providers/alerts";
import { useContext, useState, useEffect, useCallback } from "react";
import { apiRequest } from "utils/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@chakra-ui/react";
import { Switch } from "@chakra-ui/react";
import { isPermissionEnabled, permissionConstants, hasPermission } from "utils/permissionHelper";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { useRoles } from "hooks/useRoles";
import { getErrorMessage } from "utils/errorHandler";
import { LuInfo } from "react-icons/lu";

interface PermissionData {
  id: string;
  name: string;
  tooltip: string;
  isActive: boolean;
}

const LenderPermissions = ({ userType }: { userType: "lender" | "borrower" }) => {
  const { user } = useTenant();
  const [loading, setLoading] = useState(false);
  const [permissionList, setPermissionList] = useState<any[]>([]);
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [rolePermissions, setRolePermissions] = useState<any[]>([]);
  const { showSuccess, showError } = useContext(AlertsContext);

  const { roles: lenderRoles, invalidate: invalidateLender } = useRoles({
    userType: "lender",
    company_id: user.company.id,
  });
  const { roles: borrowerRoles, invalidate: invalidateBorrower } = useRoles({
    userType: "borrower",
    company_id: user.company.id,
  });
  const { permissions } = useRoles({
    userType: userType,
    company_id: user.company.id,
  });

  const [editRole, setEditRole] = useState(false);

  // Combine both role types
  const allRoles = [...(lenderRoles || []), ...(borrowerRoles || [])];

  const invalidate = useCallback(async () => {
    await invalidateLender();
    await invalidateBorrower();
  }, [invalidateLender, invalidateBorrower]);

  // Handle role creation events
  useEffect(() => {
    const handleCreateRole = async (event: Event) => {
      const customEvent = event as CustomEvent<{ type: "lender" | "borrower" }>;
      const { type } = customEvent.detail;
      try {
        const { role } = await apiRequest("/supabase/roles/insert", {
          name: `New ${type} Role`,
          company_id: user.company.id,
          company_type: type,
        });

        // Select the newly created role
        setSelectedRole(role);
        setEditRole(true);
        await invalidate();
      } catch (error) {
        console.error("Failed to create role:", error);
        showError({
          title: "Create Role Failed",
          description: "Failed to create new role. Please try again.",
        });
      }
    };

    window.addEventListener("createRole", handleCreateRole);
    return () => {
      window.removeEventListener("createRole", handleCreateRole);
    };
  }, [user.company.id, invalidate, showError]);

  const isChecked = (id: string) => {
    const foundPermission = permissionList.find(permission => permission.id === id);
    return foundPermission && foundPermission.isActive;
  };

  const PermissionItem = ({
    permission,
    changePermission,
  }: {
    permission: any;
    changePermission: () => void;
  }) => {
    return (
      <FormControl display="flex" alignItems="center">
        <Switch
          mr="10px"
          colorScheme="teal"
          isChecked={isChecked(permission.id)}
          onChange={() => changePermission()}
        ></Switch>

        <FormLabel m="0">
          {permission.name}
          {permission.tooltip && (
            <Tooltip
              hasArrow
              label={permission?.tooltip}
              aria-label={permission?.tooltip}
              placement="right"
            >
              <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                <LuInfo />
              </Icon>
            </Tooltip>
          )}
        </FormLabel>
      </FormControl>
    );
  };

  const PermissionSection = ({
    title,
    permissions,
  }: {
    title: string;
    permissions: PermissionData[];
  }) => {
    return (
      <Card>
        <CardHeader pb="0">
          <Heading size="sm" textTransform="capitalize" color="teal.500">
            {title}
          </Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={[1]} spacing={2}>
            {permissions.map(permission => (
              <PermissionItem
                key={`permission-item-${permission.id}`}
                permission={permission}
                changePermission={() => handlePermissionChange(permission)}
              />
            ))}
          </SimpleGrid>
        </CardBody>
      </Card>
    );
  };

  const handlePermissionChange = async (permission: any) => {
    setPermissionList(prevPermissions => {
      const permissionIndex = prevPermissions.findIndex(p => p.id === permission.id);

      if (permissionIndex !== -1) {
        const updatedPermissions = [...prevPermissions];
        updatedPermissions[permissionIndex] = {
          ...updatedPermissions[permissionIndex],
          isActive: !updatedPermissions[permissionIndex].isActive,
        };
        return updatedPermissions;
      } else {
        return [...prevPermissions, { ...permission, isActive: true }];
      }
    });
  };

  const organizedData: Record<string, PermissionData[]> =
    permissions?.reduce((groupedPermissions: Record<string, PermissionData[]>, permission: any) => {
      // Get the section name from the permission
      const sectionName = permission.permission_section;

      // Initialize array for this section if it doesn't exist
      if (!groupedPermissions[sectionName]) {
        groupedPermissions[sectionName] = [];
      }

      // Add formatted permission to the section
      groupedPermissions[sectionName].push({
        id: permission.id,
        name: permission.permission_description,
        tooltip: permission.permission_tooltip,
        isActive: false,
      });

      return groupedPermissions;
    }, {}) ?? {};

  const selectRole = async (event: any) => {
    setEditRole(false);
    const selectedRoleId = event.target.value;
    const roleObj = allRoles.find((r: any) => r.id === selectedRoleId);
    setSelectedRole(roleObj);
    try {
      if (roleObj) {
        const rolePermissions = await apiRequest("/supabase/role_permissions/select", {
          roleId: roleObj.id,
        });
        const rolePermissionsData = rolePermissions.data;
        if (rolePermissionsData.length > 0) {
          setRolePermissions(rolePermissionsData);
          const enabledPermissions = rolePermissionsData
            .filter((item: any) => item.is_active === true)
            ?.map((item: any) => ({
              id: item.permission_id,
              isActive: item.is_active,
            }));
          if (enabledPermissions.length > 0) {
            const permissionIds = enabledPermissions?.map((p: any) => ({
              id: p.id,
              isActive: p.isActive,
            }));
            setPermissionList(permissionIds);
          } else setPermissionList([]);
        } else setPermissionList([]);
      } else {
        setPermissionList([]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const roleNameChange = (event: any) => {
    setSelectedRole((role: any) => ({ ...role, name: event?.target.value }));
  };

  const checkEditPermission = () => {
    if (hasPermission(user, permissionConstants.editUserRolesPermissions)) {
      return true;
    }
    if (!isPermissionEnabled(user)) {
      // no permissions
      return true;
    }
    return false;
  };

  const saveChanges = async () => {
    if (checkEditPermission()) {
      setLoading(true);
      const changedPermission = rolePermissions.filter(role => {
        const correspondingPermission = permissionList.find(p => p.id === role.permission_id);
        return correspondingPermission && correspondingPermission.isActive !== role.is_active;
      });

      const nonExistingRolePermissions = permissionList.filter(
        role => !rolePermissions.some(permission => permission.permission_id === role.id)
      );
      try {
        if (changedPermission.length > 0) {
          for (const permission of changedPermission) {
            await apiRequest("/supabase/role_permissions/update", {
              id: permission.id,
              active: !permission.is_active,
            });
          }
        }
        if (nonExistingRolePermissions.length > 0) {
          for (const permission of nonExistingRolePermissions) {
            await apiRequest("/supabase/role_permissions/insert", {
              roleId: selectedRole.id,
              permissionId: permission.id,
              active: permission.isActive,
            });
          }
        }
        await apiRequest("/supabase/roles/update", {
          id: selectedRole.id,
          name: selectedRole.name,
          permissions: {},
        });
      } catch (error) {
        console.error(error);
      } finally {
        // window.location.reload();
        invalidate();
        showSuccess({
          title: "Permission modifed successfully",
          description: "Permissions modified successfully for selected role.",
        });
        setRolePermissions([]);
        setLoading(false);
        setEditRole(false);
      }
    } else {
      setLoading(false);
      showError({
        title: "Edit permissions failed",
        description: "You do not have permission to modify permissions",
      });
    }
  };

  const deleteRole = async () => {
    if (hasPermission(user, permissionConstants.deleteUserRolesPermissions)) {
      if (!selectedRole || !selectedRole?.id) {
        showError({
          title: "Delete role failed",
          description: "Please select a role to delete",
        });
      } else {
        setLoading(true);
        try {
          await apiRequest("/supabase/roles/delete", {
            id: selectedRole.id,
          });
          showSuccess({
            title: "Role deleted successfully",
            description: "Role deleted successfully",
          });
          setLoading(false);
        } catch (error) {
          console.error(error);
          showError({
            title: "Delete role failed",
            description: getErrorMessage(error),
          });
          setLoading(false);
        } finally {
          setSelectedRole(null);
          setPermissionList([]);
          invalidate();
        }
      }
    } else {
      showError({
        title: "Delete role failed",
        description: "You do not have permission to delete a role",
      });
    }
  };
  return (
    <Box>
      <Flex my={10} gap={4}>
        {!editRole ? (
          <>
            <Select placeholder="Select role" onChange={selectRole} value={selectedRole?.id || ""}>
              {allRoles?.map((role: any) => (
                <option key={role.id} value={role.id}>
                  {role.company_type === "lender" ? "Lender - " : "Borrower - "}
                  {role.name}
                </option>
              ))}
            </Select>

            {selectedRole && (
              <Box>
                <Button onClick={() => setEditRole(true)}>
                  <FontAwesomeIcon icon={["far", "edit"]} /> &nbsp; Edit Text
                </Button>
              </Box>
            )}
          </>
        ) : (
          <>
            <Input
              variant="outline"
              placeholder="Role name"
              isDisabled={
                !selectedRole ||
                !selectedRole?.name ||
                !hasPermission(user, permissionConstants.editUserRolesPermissions)
              }
              value={selectedRole ? selectedRole?.name : ""}
              onChange={roleNameChange}
            />

            <Button
              onClick={() => setEditRole(false)}
              isDisabled={!hasPermission(user, permissionConstants.editUserRolesPermissions)}
            >
              Cancel
            </Button>
          </>
        )}

        {selectedRole && (
          <>
            <Box>
              <Button
                variant={getCustomButtonVariant(user)}
                isLoading={loading}
                isDisabled={
                  !selectedRole ||
                  !selectedRole?.name ||
                  !hasPermission(user, permissionConstants.editUserRolesPermissions)
                }
                onClick={saveChanges}
              >
                <FontAwesomeIcon icon={["far", "save"]} /> &nbsp; Save Changes
              </Button>
            </Box>
          </>
        )}
      </Flex>

      <SimpleGrid columns={[1, 2, 3]} gap={4}>
        {Object.entries(organizedData)
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([title, items]) => {
            return (
              <PermissionSection
                key={`permissions-${title}`}
                title={title}
                permissions={items.sort((a, b) => a.name.localeCompare(b.name))}
              />
            );
          })}
      </SimpleGrid>

      {selectedRole && (
        <Flex direction="column" alignItems="flex-end" mt={10}>
          <Box>
            <Button
              background={"none"}
              color={"red"}
              border={"none"}
              _hover={{ borderBottom: "2px solid red", color: "red" }}
              onClick={deleteRole}
              isLoading={loading}
            >
              <FontAwesomeIcon
                icon={["far", "trash"]}
                style={{ marginRight: "5px", color: "red" }}
              />
              Delete Role
            </Button>
          </Box>
        </Flex>
      )}
    </Box>
  );
};

export default LenderPermissions;
